import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Calendar as CalendarIcon, Settings2 } from "lucide-react";
import { format } from "date-fns";

const SetupPage = () => {
  const [events, setEvents] = useState([]);
  const [playdays, setPlaydays] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Event Dialog
  const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    name: "",
    fee: 3.0,
    age_group: "",
    order: 0
  });

  // Playday Dialog
  const [isPlaydayDialogOpen, setIsPlaydayDialogOpen] = useState(false);
  const [editingPlayday, setEditingPlayday] = useState(null);
  const [playdayForm, setPlaydayForm] = useState({
    date: new Date(),
    name: "",
    event_ids: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, playdaysRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/playdays`)
      ]);
      setEvents(eventsRes.data);
      setPlaydays(playdaysRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const seedDefaultEvents = async () => {
    try {
      const res = await axios.post(`${API}/events/seed-defaults`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error("Failed to seed events");
    }
  };

  // Event handlers
  const handleEventSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, eventForm);
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/events`, eventForm);
        toast.success("Event created");
      }
      fetchData();
      closeEventDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleEventDelete = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${API}/events/${id}`);
      toast.success("Event deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const openEditEventDialog = (event) => {
    setEditingEvent(event);
    setEventForm({
      name: event.name,
      fee: event.fee,
      age_group: event.age_group || "",
      order: event.order
    });
    setIsEventDialogOpen(true);
  };

  const closeEventDialog = () => {
    setIsEventDialogOpen(false);
    setEditingEvent(null);
    setEventForm({ name: "", fee: 3.0, age_group: "", order: 0 });
  };

  // Playday handlers
  const handlePlaydaySubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        date: format(playdayForm.date, "yyyy-MM-dd"),
        name: playdayForm.name || `Play Day ${format(playdayForm.date, "MMM d, yyyy")}`,
        event_ids: playdayForm.event_ids
      };
      
      if (editingPlayday) {
        await axios.put(`${API}/playdays/${editingPlayday.id}`, data);
        toast.success("Play day updated");
      } else {
        await axios.post(`${API}/playdays`, data);
        toast.success("Play day created");
      }
      fetchData();
      closePlaydayDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handlePlaydayDelete = async (id) => {
    if (!window.confirm("Delete this play day?")) return;
    try {
      await axios.delete(`${API}/playdays/${id}`);
      toast.success("Play day deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete play day");
    }
  };

  const openEditPlaydayDialog = (playday) => {
    setEditingPlayday(playday);
    setPlaydayForm({
      date: new Date(playday.date),
      name: playday.name,
      event_ids: playday.event_ids
    });
    setIsPlaydayDialogOpen(true);
  };

  const closePlaydayDialog = () => {
    setIsPlaydayDialogOpen(false);
    setEditingPlayday(null);
    setPlaydayForm({ date: new Date(), name: "", event_ids: [] });
  };

  const toggleEventSelection = (eventId) => {
    setPlaydayForm(prev => ({
      ...prev,
      event_ids: prev.event_ids.includes(eventId)
        ? prev.event_ids.filter(id => id !== eventId)
        : [...prev.event_ids, eventId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="setup-page">
      <div>
        <h1 className="font-heading font-bold text-3xl text-text-primary">Setup</h1>
        <p className="text-text-secondary mt-1">Manage events and play day dates</p>
      </div>

      <Tabs defaultValue="events" className="space-y-6">
        <TabsList className="bg-secondary/50">
          <TabsTrigger value="events" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Settings2 className="h-4 w-4 mr-2" />
            Events
          </TabsTrigger>
          <TabsTrigger value="playdays" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <CalendarIcon className="h-4 w-4 mr-2" />
            Play Days
          </TabsTrigger>
        </TabsList>

        {/* Events Tab */}
        <TabsContent value="events" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-text-secondary">{events.length} events configured</p>
            <div className="flex gap-3">
              {events.length === 0 && (
                <Button variant="outline" onClick={seedDefaultEvents} data-testid="seed-events-btn">
                  Load Default Events
                </Button>
              )}
              <Dialog open={isEventDialogOpen} onOpenChange={setIsEventDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-primary text-primary-foreground hover:bg-primary-hover" data-testid="add-event-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Event
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-heading">
                      {editingEvent ? "Edit Event" : "Add Event"}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleEventSubmit} className="space-y-4" data-testid="event-form">
                    <div className="space-y-2">
                      <Label>Event Name</Label>
                      <Input
                        value={eventForm.name}
                        onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
                        placeholder="e.g., Mini Barrels"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Entry Fee ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={eventForm.fee}
                          onChange={(e) => setEventForm({ ...eventForm, fee: parseFloat(e.target.value) })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Order</Label>
                        <Input
                          type="number"
                          value={eventForm.order}
                          onChange={(e) => setEventForm({ ...eventForm, order: parseInt(e.target.value) })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Age Group (Optional)</Label>
                      <Select
                        value={eventForm.age_group || "all"}
                        onValueChange={(value) => setEventForm({ ...eventForm, age_group: value === "all" ? "" : value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All ages" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Ages</SelectItem>
                          <SelectItem value="mini">Mini</SelectItem>
                          <SelectItem value="junior">Junior</SelectItem>
                          <SelectItem value="senior">Senior</SelectItem>
                          <SelectItem value="adult">Adult</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={closeEventDialog} className="flex-1">
                        Cancel
                      </Button>
                      <Button type="submit" className="flex-1 bg-primary text-primary-foreground hover:bg-primary-hover" data-testid="event-submit">
                        {editingEvent ? "Update" : "Add"}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-primary/20 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead className="font-heading font-bold text-text-primary w-16">#</TableHead>
                  <TableHead className="font-heading font-bold text-text-primary">Event Name</TableHead>
                  <TableHead className="font-heading font-bold text-text-primary">Age Group</TableHead>
                  <TableHead className="font-heading font-bold text-text-primary">Fee</TableHead>
                  <TableHead className="font-heading font-bold text-text-primary text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-text-secondary">
                      No events configured. Click "Load Default Events" to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  events.map((event) => (
                    <TableRow key={event.id} className="hover:bg-surface-muted" data-testid={`event-row-${event.id}`}>
                      <TableCell className="text-text-muted">{event.order}</TableCell>
                      <TableCell className="font-medium text-text-primary">{event.name}</TableCell>
                      <TableCell>
                        {event.age_group ? (
                          <span className="px-2 py-1 bg-primary/20 text-primary-foreground rounded text-xs font-medium capitalize">
                            {event.age_group}
                          </span>
                        ) : (
                          <span className="text-text-muted">All</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono">${event.fee.toFixed(2)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditEventDialog(event)}
                            className="h-8 w-8 p-0"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEventDelete(event.id)}
                            className="h-8 w-8 p-0 text-status-error hover:text-status-error"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Play Days Tab */}
        <TabsContent value="playdays" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-text-secondary">{playdays.length} play days scheduled</p>
            <Dialog open={isPlaydayDialogOpen} onOpenChange={setIsPlaydayDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary text-primary-foreground hover:bg-primary-hover" data-testid="add-playday-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Play Day
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading">
                    {editingPlayday ? "Edit Play Day" : "Add Play Day"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handlePlaydaySubmit} className="space-y-4" data-testid="playday-form">
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(playdayForm.date, "PPP")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={playdayForm.date}
                          onSelect={(date) => date && setPlaydayForm({ ...playdayForm, date })}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Name (Optional)</Label>
                    <Input
                      value={playdayForm.name}
                      onChange={(e) => setPlaydayForm({ ...playdayForm, name: e.target.value })}
                      placeholder={`Play Day ${format(playdayForm.date, "MMM d, yyyy")}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Select Events</Label>
                    <div className="max-h-48 overflow-y-auto border rounded-lg p-3 space-y-2">
                      {events.map((event) => (
                        <div key={event.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`event-${event.id}`}
                            checked={playdayForm.event_ids.includes(event.id)}
                            onCheckedChange={() => toggleEventSelection(event.id)}
                          />
                          <label
                            htmlFor={`event-${event.id}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            {event.name}
                            {event.age_group && (
                              <span className="ml-2 text-xs text-text-muted">({event.age_group})</span>
                            )}
                          </label>
                          <span className="text-xs text-text-muted font-mono">${event.fee}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-text-muted">
                      {playdayForm.event_ids.length} events selected
                    </p>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={closePlaydayDialog} className="flex-1">
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1 bg-primary text-primary-foreground hover:bg-primary-hover" data-testid="playday-submit">
                      {editingPlayday ? "Update" : "Create"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white rounded-xl border border-primary/20 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead className="font-heading font-bold text-text-primary">Date</TableHead>
                  <TableHead className="font-heading font-bold text-text-primary">Name</TableHead>
                  <TableHead className="font-heading font-bold text-text-primary">Events</TableHead>
                  <TableHead className="font-heading font-bold text-text-primary text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {playdays.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-text-secondary">
                      No play days scheduled yet
                    </TableCell>
                  </TableRow>
                ) : (
                  playdays.map((playday) => (
                    <TableRow key={playday.id} className="hover:bg-surface-muted" data-testid={`playday-row-${playday.id}`}>
                      <TableCell className="font-mono text-text-primary">{playday.date}</TableCell>
                      <TableCell className="font-medium text-text-primary">{playday.name}</TableCell>
                      <TableCell>
                        <span className="px-2 py-1 bg-secondary text-text-primary rounded text-xs font-medium">
                          {playday.event_ids.length} events
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditPlaydayDialog(playday)}
                            className="h-8 w-8 p-0"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handlePlaydayDelete(playday.id)}
                            className="h-8 w-8 p-0 text-status-error hover:text-status-error"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SetupPage;
